import {
  QueryClient,
  useInfiniteQuery,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query'
import { httpClient } from '@/api/http-client'
import { FiscalNote, FiscalNoteStatus } from '@/types'
import { Pagination } from '@/types/commons'

type ReadFiscalNoteListParams = {
  company_key: string
  start_date?: string
  end_date?: string
  borrower_name?: string
  borrower_tax_id?: string
  status?: FiscalNoteStatus
  page?: number
  page_size?: number
}

export async function readFiscalNoteList({
  company_key,
  ...params
}: ReadFiscalNoteListParams) {
  const response = await httpClient('accounting-dealer').get<
    Pagination<FiscalNote>
  >(`/v3/company/${company_key}/fiscal-notes`, {
    params,
  })

  return response.data
}

export const FISCAL_NOTE_LIST_QUERY_KEY = ['fiscal-note-list'] as const
export const FISCAL_NOTE_LIST_INFINITE_QUERY_KEY = [
  'fiscal-note-list-infinite',
] as const

export function invalidateFiscalNoteListQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: FISCAL_NOTE_LIST_QUERY_KEY })
  queryClient.invalidateQueries({
    queryKey: FISCAL_NOTE_LIST_INFINITE_QUERY_KEY,
  })
}

export function useReadFiscalNoteList(
  params: ReadFiscalNoteListParams,
  options?: Partial<UseQueryOptions<Pagination<FiscalNote>>>,
) {
  return useQuery({
    queryKey: [...FISCAL_NOTE_LIST_QUERY_KEY, params],
    queryFn: () => readFiscalNoteList(params),
    ...options,
  })
}

const PAGE_SIZE = 50

export function useInfiniteReadFiscalNoteList(
  params: Omit<ReadFiscalNoteListParams, 'page' | 'page_size'>,
) {
  return useInfiniteQuery({
    queryKey: [...FISCAL_NOTE_LIST_INFINITE_QUERY_KEY, params],
    queryFn: ({ pageParam }) =>
      readFiscalNoteList({ ...params, page: pageParam, page_size: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.count / lastPage.page_size)
      return lastPage.page < totalPages ? lastPage.page + 1 : undefined
    },
  })
}
